import React from 'react';

/**
 * Petzify custom icon set.
 *
 * Hand-drawn 24x24 line icons (stroke = currentColor) used across the home
 * page, navbar and app-download section instead of emojis or generic icon
 * packs. Pass `className` for sizing/colour, e.g. "w-6 h-6 text-teal-600".
 */

const Stroke = ({ className = 'w-6 h-6', label, children }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.7"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    role={label ? 'img' : undefined}
    aria-label={label}
    aria-hidden={label ? undefined : 'true'}
  >
    {children}
  </svg>
);

export const PawIcon = ({ className, label }) => (
  <svg
    viewBox="0 0 24 24"
    fill="currentColor"
    className={className}
    role={label ? 'img' : undefined}
    aria-label={label}
    aria-hidden={label ? undefined : 'true'}
  >
    <ellipse cx="12" cy="15.8" rx="4.6" ry="3.8" />
    <ellipse cx="5.6" cy="11.2" rx="1.9" ry="2.3" transform="rotate(-20 5.6 11.2)" />
    <ellipse cx="9.7" cy="7.6" rx="2" ry="2.5" transform="rotate(-8 9.7 7.6)" />
    <ellipse cx="14.3" cy="7.6" rx="2" ry="2.5" transform="rotate(8 14.3 7.6)" />
    <ellipse cx="18.4" cy="11.2" rx="1.9" ry="2.3" transform="rotate(20 18.4 11.2)" />
  </svg>
);

export const BoneIcon = (props) => (
  <Stroke {...props}>
    <path d="M9.2 8.2a2.6 2.6 0 10-3.7 3.2 2.6 2.6 0 102.5 4.3l.4-.4 6.4-6.4.4-.4a2.6 2.6 0 104.3 2.5 2.6 2.6 0 10-3.2-3.7 2.6 2.6 0 10-3.7-3.2 2.6 2.6 0 102.5 4.3" />
    <path d="M8.4 15.3l6.9-6.9" />
  </Stroke>
);

export const BowlIcon = (props) => (
  <Stroke {...props}>
    <path d="M4 12h16a8 8 0 01-4.6 6.6l.4 2.4H8.2l.4-2.4A8 8 0 014 12z" />
    <path d="M8.5 8.8c0-1 .8-1.8 1.8-1.8s1.7.8 1.7 1.8M13 6.5c0-1 .8-1.8 1.8-1.8s1.7.8 1.7 1.8" />
  </Stroke>
);

export const BallIcon = (props) => (
  <Stroke {...props}>
    <circle cx="12" cy="12" r="8.2" />
    <path d="M5.7 6.7c3.1 2.6 3.1 8 0 10.6M18.3 6.7c-3.1 2.6-3.1 8 0 10.6" />
  </Stroke>
);

export const PillIcon = (props) => (
  <Stroke {...props}>
    <path d="M6.2 17.8a4.4 4.4 0 010-6.2l5.4-5.4a4.4 4.4 0 016.2 6.2l-5.4 5.4a4.4 4.4 0 01-6.2 0z" />
    <path d="M8.9 8.9l6.2 6.2" />
  </Stroke>
);

export const ScissorsIcon = (props) => (
  <Stroke {...props}>
    <circle cx="6" cy="6.5" r="2.4" />
    <circle cx="6" cy="17.5" r="2.4" />
    <path d="M8.1 7.9L20 17.5M8.1 16.1L20 6.5" />
  </Stroke>
);

export const KennelIcon = (props) => (
  <Stroke {...props}>
    <path d="M3 10.8L12 4l9 6.8V20h-5.2v-3.4a3.8 3.8 0 00-7.6 0V20H3v-9.2z" />
    <path d="M12 4v3.2" />
  </Stroke>
);

export const VanIcon = (props) => (
  <Stroke {...props}>
    <path d="M2.5 16.2V7.5h11.3v8.7M13.8 10h4.2l3 3.2v3h-2.1" />
    <circle cx="6.8" cy="17.4" r="1.9" />
    <circle cx="16.9" cy="17.4" r="1.9" />
    <path d="M8.7 16.2H15M2.5 16.2h2.4" />
  </Stroke>
);

export const AppleIcon = (props) => (
  <Stroke {...props}>
    <path d="M12 7.4c-1.2-2.4-5.3-2.3-6.6.8-1.4 3.4 1.4 8.3 4.2 10.2 1.5 1 3.3 1 4.8 0 2.8-1.9 5.6-6.8 4.2-10.2-1.3-3.1-5.4-3.2-6.6-.8z" />
    <path d="M12 7.4c0-2.1 1.1-3.6 3.1-4.4" />
  </Stroke>
);

export const StethoscopeIcon = (props) => (
  <Stroke {...props}>
    <path d="M4.5 3.5H6v5a4.5 4.5 0 009 0v-5h1.5" />
    <path d="M10.5 13v2.7a4.8 4.8 0 009.6 0v-2.2" />
    <circle cx="20.1" cy="10.6" r="2.1" />
  </Stroke>
);

export const BotIcon = (props) => (
  <Stroke {...props}>
    <rect x="5" y="8.5" width="14" height="10" rx="3" />
    <path d="M12 8.5V5.8M12 5.8a1.4 1.4 0 110-2.8 1.4 1.4 0 010 2.8zM5 13H3M21 13h-2" />
    <circle cx="9.6" cy="13" r="0.6" fill="currentColor" />
    <circle cx="14.4" cy="13" r="0.6" fill="currentColor" />
    <path d="M9.8 16h4.4" />
  </Stroke>
);

export const HeartIcon = (props) => (
  <Stroke {...props}>
    <path d="M12 20.2S3.8 15.3 2.6 10.6A5.1 5.1 0 0112 6.7a5.1 5.1 0 019.4 3.9C20.2 15.3 12 20.2 12 20.2z" />
  </Stroke>
);

export const ChatIcon = (props) => (
  <Stroke {...props}>
    <path d="M21 11.6a7.6 7.6 0 01-7.6 7.6H4l1.9-2.9A7.6 7.6 0 1121 11.6z" />
    <path d="M8.5 12h.01M12 12h.01M15.5 12h.01" />
  </Stroke>
);

export const GiftIcon = (props) => (
  <Stroke {...props}>
    <rect x="4" y="10.2" width="16" height="9.8" rx="1.5" />
    <path d="M12 10.2V20M4 14h16" />
    <path d="M12 10.2S8.6 10 7.2 8.7a2 2 0 012.7-2.9C11.4 7.2 12 10.2 12 10.2zM12 10.2s3.4-.2 4.8-1.5a2 2 0 00-2.7-2.9C12.6 7.2 12 10.2 12 10.2z" />
  </Stroke>
);

export const BoltIcon = ({ className, label }) => (
  <svg
    viewBox="0 0 24 24"
    fill="currentColor"
    className={className}
    role={label ? 'img' : undefined}
    aria-label={label}
    aria-hidden={label ? undefined : 'true'}
  >
    <path d="M13.2 2.2L4.4 13.6h5.4l-1 8.2 8.8-11.4h-5.4l1-8.2z" />
  </svg>
);

export const BellIcon = (props) => (
  <Stroke {...props}>
    <path d="M15 17.2h4.6l-1.3-1.3a2 2 0 01-.6-1.4v-3.3a5.7 5.7 0 10-11.4 0v3.3a2 2 0 01-.6 1.4L4.4 17.2H9m6 0v.9a3 3 0 11-6 0v-.9m6 0H9" />
  </Stroke>
);

export const ShieldCheckIcon = (props) => (
  <Stroke {...props}>
    <path d="M12 2.8l7.2 3v5c0 4.6-3 8.8-7.2 10.4C7.8 19.6 4.8 15.4 4.8 10.8v-5l7.2-3z" />
    <path d="M9 11.8l2.1 2.1 4-4" />
  </Stroke>
);

export const TruckFastIcon = VanIcon;

export const ArrowRightIcon = (props) => (
  <Stroke {...props}>
    <path d="M5 12h14M13 6l6 6-6 6" />
  </Stroke>
);

export const SearchIcon = (props) => (
  <Stroke {...props}>
    <circle cx="11" cy="11" r="7" />
    <path d="M21 21l-4.8-4.8" />
  </Stroke>
);

export const DocumentIcon = (props) => (
  <Stroke {...props}>
    <path d="M7 3.5h7l4 4V20.5H7a1.5 1.5 0 01-1.5-1.5V5A1.5 1.5 0 017 3.5z" />
    <path d="M14 3.5v4h4M9 12h6M9 15.5h6" />
  </Stroke>
);

export const CheckCircleIcon = (props) => (
  <Stroke {...props}>
    <circle cx="12" cy="12" r="8.5" />
    <path d="M8.5 12.2l2.4 2.4 4.6-4.8" />
  </Stroke>
);
